/** Strings of /accounting/bank: `accBankEn` is the source, `accBankCs` must have the same keys. */
export const accBankEn = {
  accBank: {
    title: "Bank",
    subtitle:
      "Movements arrive on their own and settle the invoice whose variable symbol they carry. What needs a decision is here.",
    pullNow: "Pull now",
    queueing: "Queueing…",
    needToken: "Add an account with a Fio token first",
    errors: {
      pull: "Couldn't queue the pull.",
      rematch: "Couldn't match the inbox again.",
      addAccount: "Couldn't add the account.",
      removeAccount: "Couldn't remove the account.",
      check: "Couldn't run the check.",
      tie: "Couldn't tie the movement.",
      saveToken: "Couldn't save the token.",
      place: "Couldn't place the payment.",
      revert: "Couldn't take the match back.",
      putAside: "Couldn't put the movement aside.",
      bringBack: "Couldn't bring the movement back.",
      removeRule: "Couldn't remove the rule.",
      rebuild: "Couldn't rebuild the payments.",
      accept: "Couldn't take Fakturoid's payment.",
      unaccept: "Couldn't take it back.",
      pair: "Couldn't pair the payment.",
      undoRebuild: "Couldn't undo the rebuild.",
    },
    reason: {
      variable_symbol: "variable symbol",
      exact_amount: "exact amount",
      known_counterparty: "pays from this account",
      card_amount: "card amount",
      supplier_name: "same supplier",
    },
    accounts: {
      title: "Accounts",
      description:
        'Use a Fio token of the read-only ("sledovat účet") kind — read access, never a payment token — and issue it for Hub alone. A bookmark read empties Fio\'s queue for that token, so a token shared with another system means the two of you split the movements between you.',
      empty: "No bank account yet.",
      account: "Account",
      number: "Number",
      token: "Token",
      reads: "Reads",
      autoMatching: "Auto-matching",
      lastPull: "Last pull",
      last30: "last 30 days",
      sinceBookmark: "since bookmark",
      on: "on",
      off: "off",
      never: "never",
      remove: "remove",
      accountName: "Account name",
      accountNamePlaceholder: "Fio — operations",
      accountNumber: "Account number",
      bankCode: "Bank code",
      currency: "Currency",
      fioToken: "Fio API token (read-only, 64 characters)",
      fioTokenPlaceholder: "stored encrypted, never shown again",
      saving: "Saving…",
      add: "Add account",
      backfillFrom: "Backfill from",
      backfillTo: "to",
      fetchHistory: "Fetch history",
      historyHint:
        "Fio serves the last 90 days straight away. For anything older, unlock the token in the internet banking first (Settings → API, the padlock) — the history then stays open for ten minutes.",
    },
    token: {
      set: "set",
      missing: "missing",
      replace: "replace",
      add: "add",
      placeholder: "Fio token",
      save: "Save",
      cancel: "cancel",
    },
    movements: {
      titleInbox: "Needs a decision",
      titleIgnored: "Put aside",
      titleAll: "All movements",
      count: "{{total}} movement(s)",
      placedOf: "{{matched}} of {{checked}} placed",
      releasedNote: ", {{count}} placed again by amount (shared symbol)",
      restoredNote: ", {{count}} lost payment(s) restored",
      matching: "Matching…",
      matchAgain: "Match again",
      viewInbox: "Inbox",
      viewAll: "All",
      viewIgnored: "Put aside",
      emptyInbox: "Nothing waiting — every movement is placed or put aside.",
      empty: "Nothing here.",
      date: "Date",
      counterparty: "Counterparty",
      vs: "Var. symbol",
      amount: "Amount",
      status: "Status",
      card: "card",
      sourceFakturoid: "from Fakturoid",
      sourceAuto: "automatic",
      sourceManual: "by hand",
      onlyLink: " · link only",
      undo: "undo",
      putAsideRule: "put aside · rule",
      putAside: "put aside",
      bringBack: "bring back",
      close: "Close",
      decide: "Decide…",
      couldBe: "Could be",
      looking: "Looking…",
      noCandidates:
        "Nothing looks like a match — put the movement aside if it isn't a document payment.",
      tieTo: "Tie to {{number}}",
      placeOn: "Place on {{number}}",
      reasonPlaceholder: "Not a payment? Say why — bank fee, transfer, salary…",
      alwaysCard: "always for card charges at {{merchant}}",
      alwaysAccount: "always for {{account}}/{{bankCode}}",
      putAsideButton: "Put aside",
    },
    check: {
      title: "Paid in the books, seen in the bank?",
      description:
        "Every document paid in the year against the account. What Fakturoid calls paid with nothing on the account that fits is paid from elsewhere, or paired wrongly.",
      year: "Year",
      checking: "Checking…",
      check: "Check",
      tied: "tied to a movement",
      fits: "with a movement that fits",
      noMovement: "with nothing in the bank",
      paid: "Paid",
      document: "Document",
      total: "Total",
      inBank: "In the bank",
      nothingFits: "nothing fits",
      tie: "Tie",
    },
    doc: {
      expense: "Expense",
      invoice: "Invoice",
    },
    lastPull: {
      noAccount:
        "There is no bank account yet — add one below together with its Fio token. Until then a pull has nothing to do.",
      noToken:
        "No account has a token, so a pull has nowhere to read from. Add one on an account below.",
      none: "No pull has run yet. The scheduled one runs every 30 minutes.",
      failed: "The last pull failed: {{error}}",
      noDetails: "no details",
      running: "Pulling…",
      summary:
        "Last pull <0/> — imported <1>{{imported}}</1> movement(s), of which <1>{{matched}}</1> matched themselves.",
      summaryNoDate:
        "Last pull — imported <1>{{imported}}</1> movement(s), of which <1>{{matched}}</1> matched themselves.",
      nothingNew: "Nothing new. Fio only returns movements Hub doesn't have yet.",
    },
    rules: {
      title: "Put aside automatically",
      description:
        "Payments to these accounts or card charges at these merchants are never a document payment. A new rule comes from ticking „always“ when putting a movement aside. Removing a rule brings back what it put aside.",
      loading: "Loading…",
      none: "No rules.",
      card: "card: {{merchant}}",
      anyAccount: "any account",
      putAsideCount: "{{count}} put aside",
      remove: "remove",
    },
    rebuild: {
      title: "Payments from the bank",
      description:
        "Documents imported from Fakturoid get their payments from the bank statement instead of Fakturoid's pairing. Fakturoid itself stays as it is. What Fakturoid called paid and the bank doesn't show is listed below.",
      yearLabel: "Rebuild year",
      rebuilding: "Rebuilding…",
      button: "Rebuild from the bank",
      confirm: "Rebuild {{year}} payments from the bank? It can be undone.",
      last: "Last rebuild of {{year}}: <0/>, {{documents}} document(s).",
      undo: "undo the rebuild",
      confirmUndo: "Undo the rebuild and return to Fakturoid's payments?",
      notYet: "Not rebuilt yet.",
      unconfirmed: "Fakturoid called these paid, the bank shows nothing ({{count}})",
      document: "Document",
      total: "Total",
      fakturoidPayment: "Fakturoid's payment",
      noFit: "No movement in the bank fits.",
      payments: " ({{count}} payments)",
      pair: "Pair",
      paidOutside: "Paid outside this account",
      confirmKeep: "Keep Fakturoid's payment without a bank movement (cash, another account)?",
      hint: "„Pair“ pays it from that bank movement. „Paid outside this account“ keeps Fakturoid's payment (cash, another card or account). The rest stays unpaid.",
      accepted: "Kept at Fakturoid's payment ({{count}})",
      undoAccepted: "undo",
    },
  },
};

export const accBankCs: typeof accBankEn = {
  accBank: {
    title: "Banka",
    subtitle:
      "Pohyby přicházejí samy a uhradí fakturu, jejíž variabilní symbol nesou. Co vyžaduje rozhodnutí, je tady.",
    pullNow: "Stáhnout teď",
    queueing: "Řadím do fronty…",
    needToken: "Nejdřív přidejte účet s tokenem Fio",
    errors: {
      pull: "Stažení se nepodařilo zařadit do fronty.",
      rematch: "Nevyřešené pohyby se nepodařilo spárovat znovu.",
      addAccount: "Účet se nepodařilo přidat.",
      removeAccount: "Účet se nepodařilo odebrat.",
      check: "Kontrolu se nepodařilo provést.",
      tie: "Pohyb se nepodařilo přiřadit.",
      saveToken: "Token se nepodařilo uložit.",
      place: "Platbu se nepodařilo přiřadit.",
      revert: "Spárování se nepodařilo vrátit.",
      putAside: "Pohyb se nepodařilo odložit.",
      bringBack: "Pohyb se nepodařilo vrátit.",
      removeRule: "Pravidlo se nepodařilo odebrat.",
      rebuild: "Platby se nepodařilo přepočítat.",
      accept: "Platbu z Fakturoidu se nepodařilo ponechat.",
      unaccept: "Nepodařilo se to vrátit.",
      pair: "Platbu se nepodařilo spárovat.",
      undoRebuild: "Přepočet se nepodařilo vrátit zpět.",
    },
    reason: {
      variable_symbol: "variabilní symbol",
      exact_amount: "přesná částka",
      known_counterparty: "platí z tohoto účtu",
      card_amount: "částka z karty",
      supplier_name: "stejný dodavatel",
    },
    accounts: {
      title: "Účty",
      description:
        "Použijte token Fio typu pouze pro čtení („sledovat účet“) — přístup ke čtení, nikdy platební token — a vystavte ho jen pro Hub. Čtení od záložky vyprázdní frontu Fio pro daný token, takže token sdílený s jiným systémem znamená, že si pohyby mezi sebou rozdělíte.",
      empty: "Zatím žádný bankovní účet.",
      account: "Účet",
      number: "Číslo",
      token: "Token",
      reads: "Čte",
      autoMatching: "Automatické párování",
      lastPull: "Poslední stažení",
      last30: "posledních 30 dní",
      sinceBookmark: "od záložky",
      on: "zapnuto",
      off: "vypnuto",
      never: "nikdy",
      remove: "odebrat",
      accountName: "Název účtu",
      accountNamePlaceholder: "Fio — provoz",
      accountNumber: "Číslo účtu",
      bankCode: "Kód banky",
      currency: "Měna",
      fioToken: "API token Fio (pouze pro čtení, 64 znaků)",
      fioTokenPlaceholder: "uložen šifrovaně, už se nezobrazí",
      saving: "Ukládám…",
      add: "Přidat účet",
      backfillFrom: "Doplnit historii od",
      backfillTo: "do",
      fetchHistory: "Stáhnout historii",
      historyHint:
        "Fio vydá posledních 90 dní hned. U staršího období nejdřív odemkněte token v internetovém bankovnictví (Nastavení → API, zámeček) — historie pak zůstane otevřená deset minut.",
    },
    token: {
      set: "nastaven",
      missing: "chybí",
      replace: "vyměnit",
      add: "přidat",
      placeholder: "Token Fio",
      save: "Uložit",
      cancel: "zrušit",
    },
    movements: {
      titleInbox: "Čeká na rozhodnutí",
      titleIgnored: "Odloženo",
      titleAll: "Všechny pohyby",
      count: "{{total}} pohyb(ů)",
      placedOf: "{{matched}} z {{checked}} přiřazeno",
      releasedNote: ", {{count}} přiřazeno znovu podle částky (sdílený symbol)",
      restoredNote: ", {{count}} ztracených plateb obnoveno",
      matching: "Páruji…",
      matchAgain: "Spárovat znovu",
      viewInbox: "K řešení",
      viewAll: "Vše",
      viewIgnored: "Odloženo",
      emptyInbox: "Nic nečeká — každý pohyb je přiřazen nebo odložen.",
      empty: "Nic tu není.",
      date: "Datum",
      counterparty: "Protistrana",
      vs: "VS",
      amount: "Částka",
      status: "Stav",
      card: "karta",
      sourceFakturoid: "z Fakturoidu",
      sourceAuto: "automaticky",
      sourceManual: "ručně",
      onlyLink: " · jen vazba",
      undo: "vrátit",
      putAsideRule: "odloženo · pravidlo",
      putAside: "odloženo",
      bringBack: "vrátit zpět",
      close: "Zavřít",
      decide: "Rozhodnout…",
      couldBe: "Může jít o",
      looking: "Hledám…",
      noCandidates: "Nic nevypadá jako shoda — pokud nejde o úhradu dokladu, pohyb odložte.",
      tieTo: "Přiřadit k {{number}}",
      placeOn: "Uhradit {{number}}",
      reasonPlaceholder: "Není to platba? Napište proč — bankovní poplatek, převod, mzda…",
      alwaysCard: "vždy pro platby kartou u {{merchant}}",
      alwaysAccount: "vždy pro {{account}}/{{bankCode}}",
      putAsideButton: "Odložit",
    },
    check: {
      title: "Zaplaceno v účetnictví, vidět v bance?",
      description:
        "Všechny doklady zaplacené v daném roce proti účtu. Co Fakturoid označuje jako zaplacené bez odpovídajícího pohybu na účtu, bylo zaplaceno jinde, nebo je špatně spárované.",
      year: "Rok",
      checking: "Kontroluji…",
      check: "Zkontrolovat",
      tied: "přiřazeno k pohybu",
      fits: "s pohybem, který odpovídá",
      noMovement: "bez pohybu v bance",
      paid: "Zaplaceno",
      document: "Doklad",
      total: "Celkem",
      inBank: "V bance",
      nothingFits: "nic neodpovídá",
      tie: "Přiřadit",
    },
    doc: {
      expense: "Výdaj",
      invoice: "Faktura",
    },
    lastPull: {
      noAccount:
        "Zatím není žádný bankovní účet — přidejte ho níže spolu s tokenem Fio. Do té doby nemá stažení co dělat.",
      noToken:
        "Žádný účet nemá token, takže stažení nemá odkud číst. Přidejte ho u některého účtu níže.",
      none: "Zatím neproběhlo žádné stažení. Plánované běží každých 30 minut.",
      failed: "Poslední stažení selhalo: {{error}}",
      noDetails: "bez podrobností",
      running: "Stahuji…",
      summary:
        "Poslední stažení <0/> — importováno <1>{{imported}}</1> pohyb(ů), z toho <1>{{matched}}</1> se spárovalo samo.",
      summaryNoDate:
        "Poslední stažení — importováno <1>{{imported}}</1> pohyb(ů), z toho <1>{{matched}}</1> se spárovalo samo.",
      nothingNew: "Nic nového. Fio vrací jen pohyby, které Hub ještě nemá.",
    },
    rules: {
      title: "Automaticky odloženo",
      description:
        "Platby na tyto účty nebo platby kartou u těchto obchodníků nejsou nikdy úhradou dokladu. Nové pravidlo vznikne zaškrtnutím „vždy“ při odložení pohybu. Odebráním pravidla se vrátí to, co odložilo.",
      loading: "Načítám…",
      none: "Žádná pravidla.",
      card: "karta: {{merchant}}",
      anyAccount: "jakýkoli účet",
      putAsideCount: "{{count}} odloženo",
      remove: "odebrat",
    },
    rebuild: {
      title: "Platby z banky",
      description:
        "Doklady importované z Fakturoidu dostanou platby z bankovního výpisu místo párování z Fakturoidu. Samotný Fakturoid zůstává beze změny. Co Fakturoid označil za zaplacené a banka to neukazuje, je vypsáno níže.",
      yearLabel: "Rok přepočtu",
      rebuilding: "Přepočítávám…",
      button: "Přepočítat z banky",
      confirm: "Přepočítat platby za rok {{year}} z banky? Lze to vrátit zpět.",
      last: "Poslední přepočet roku {{year}}: <0/>, dokladů: {{documents}}.",
      undo: "vrátit přepočet",
      confirmUndo: "Vrátit přepočet zpět a vrátit se k platbám z Fakturoidu?",
      notYet: "Zatím nepřepočítáno.",
      unconfirmed: "Fakturoid je označil za zaplacené, banka nic neukazuje ({{count}})",
      document: "Doklad",
      total: "Celkem",
      fakturoidPayment: "Platba z Fakturoidu",
      noFit: "Žádný pohyb v bance neodpovídá.",
      payments: " (plateb: {{count}})",
      pair: "Spárovat",
      paidOutside: "Zaplaceno mimo tento účet",
      confirmKeep: "Ponechat platbu z Fakturoidu bez bankovního pohybu (hotovost, jiný účet)?",
      hint: "„Spárovat“ uhradí doklad z daného bankovního pohybu. „Zaplaceno mimo tento účet“ ponechá platbu z Fakturoidu (hotovost, jiná karta nebo účet). Ostatní zůstane neuhrazené.",
      accepted: "Ponecháno podle platby z Fakturoidu ({{count}})",
      undoAccepted: "vrátit",
    },
  },
};
